import { Link } from "react-router-dom";
import { newsList } from "@/mocks/news";
import { movieList } from "@/mocks/movies";
import { communityList } from "@/mocks/community";
import HeroCarousel from "./components/HeroCarousel";
import SectionHeading from "@/components/feature/SectionHeading";
import MovieCard from "@/components/feature/MovieCard";
import useDocumentMeta from "@/hooks/useDocumentMeta";

export default function Home() {
  useDocumentMeta(
    "色花堂－多元化娱乐内容与互动社区平台",
    "色花堂提供丰富的娱乐资讯、影视电视剧推荐、互动交流社区，满足用户多样化的娱乐需求与信息分享。",
  );
  const latestNews = [...newsList]
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, 5);
  const featured = latestNews[0];
  const restNews = latestNews.slice(1);

  const hotMovies = [...movieList].sort((a, b) => b.popularity - a.popularity).slice(0, 5);

  const hotPosts = [...communityList].sort((a, b) => b.views - a.views).slice(0, 10);

  const quickLinks = [
    { to: "/news", icon: "ri-newspaper-line", label: "娱乐资讯", desc: "明星 · 行业 · 活动" },
    { to: "/movies", icon: "ri-movie-2-line", label: "影视推荐", desc: "剧集 · 电影 · 口碑榜" },
    { to: "/community", icon: "ri-chat-smile-2-line", label: "互动社区", desc: "评论 · 八卦 · 交流" },
    { to: "/about", icon: "ri-information-line", label: "关于我们", desc: "平台定位 · 联系方式" },
  ];

  return (
    <div className="pb-4">
      <HeroCarousel />

      <div className="px-4 md:px-8">
        <div className="mx-auto w-full max-w-[1280px]">
          {/* 快捷入口 */}
          <section className="grid grid-cols-2 gap-3 py-8 md:grid-cols-4 md:gap-4">
            {quickLinks.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 cursor-pointer"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <span className="min-w-0">
                  <span className="block font-heading text-sm font-bold text-foreground-950">{item.label}</span>
                  <span className="block truncate text-xs text-foreground-500">{item.desc}</span>
                </span>
              </Link>
            ))}
          </section>

          {/* 娱乐资讯精选 */}
          <section className="py-6">
            <SectionHeading
              eyebrow="TODAY"
              title="娱乐资讯精选"
              description="聚焦最新明星动态、行业趋势与活动报道，第一时间掌握娱乐风向。"
              linkTo="/news"
            />
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
              <article className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:col-span-2">
                <Link to={`/news/detail/${featured.id}`} className="block cursor-pointer" aria-label={featured.title}>
                  <div className="h-52 w-full overflow-hidden md:h-60">
                    <img
                      src={featured.cover}
                      alt={featured.title}
                      title={`${featured.title} - 色花堂娱乐资讯`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                </Link>
                <div className="p-5">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                    <span className="rounded-full bg-primary-500 px-2 py-0.5 font-medium text-background-50">
                      {featured.category}
                    </span>
                    <span>{featured.date}</span>
                  </div>
                  <h3 className="mt-3 font-heading text-lg font-extrabold leading-snug text-foreground-950">
                    <Link
                      to={`/news/detail/${featured.id}`}
                      className="line-clamp-2 transition-colors hover:text-primary-700 cursor-pointer"
                    >
                      {featured.title}
                    </Link>
                  </h3>
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-500">
                    {featured.summary}
                  </p>
                </div>
              </article>

              <div className="flex flex-col gap-4 lg:col-span-3">
                {restNews.map((news) => (
                  <article
                    key={news.id}
                    className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 sm:p-4"
                  >
                    <Link to={`/news/detail/${news.id}`} className="shrink-0 cursor-pointer" aria-label={news.title}>
                      <div className="h-20 w-28 overflow-hidden rounded-md sm:h-24 sm:w-36">
                        <img
                          src={news.cover}
                          alt={news.title}
                          title={`${news.title} - 色花堂娱乐资讯`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                    </Link>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-xs text-foreground-500">
                        <span className="rounded-full bg-accent-50 px-2 py-0.5 font-medium text-accent-700">
                          {news.category}
                        </span>
                        <span>{news.date}</span>
                      </div>
                      <h3 className="mt-2 font-heading text-sm font-bold leading-snug text-foreground-950 sm:text-base">
                        <Link
                          to={`/news/detail/${news.id}`}
                          className="line-clamp-2 transition-colors hover:text-primary-700 cursor-pointer"
                        >
                          {news.title}
                        </Link>
                      </h3>
                      <p className="mt-1 hidden line-clamp-1 text-xs text-foreground-500 sm:block">
                        {news.summary}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* 影视推荐精选 */}
          <section className="py-6">
            <SectionHeading
              eyebrow="TRENDING"
              title="影视推荐精选"
              description="本周人气与口碑双高的影视作品，涵盖科幻、悬疑、爱情等多种类型。"
              linkTo="/movies"
            />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
              {hotMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </section>

          {/* 社区热门话题 */}
          <section className="py-6">
            <SectionHeading
              eyebrow="COMMUNITY"
              title="社区热门话题"
              description="来自社区的真实讨论，找到和你一样热爱娱乐的伙伴。"
              linkTo="/community"
            />
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {hotPosts.map((post, idx) => (
                <Link
                  key={post.id}
                  to={`/community/detail/${post.id}`}
                  className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 cursor-pointer"
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md font-heading text-sm font-bold ${
                      idx < 3 ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-500"
                    }`}
                  >
                    {idx + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-heading text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-700">
                      {post.title}
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line" aria-hidden="true"></i>
                        {post.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line" aria-hidden="true"></i>
                        {post.date.slice(0, 10)}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-chat-3-line" aria-hidden="true"></i>
                        {post.replyList.length} 回复
                      </span>
                    </span>
                  </span>
                  <i
                    className="ri-arrow-right-s-line shrink-0 text-xl text-foreground-300 transition-colors group-hover:text-primary-600"
                    aria-hidden="true"
                  ></i>
                </Link>
              ))}
            </div>
          </section>

          {/* 简介 CTA */}
          <section className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="flex flex-col gap-6 p-6 md:flex-row md:items-center md:justify-between md:p-10">
              <div className="max-w-2xl">
                <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
                  关于色花堂
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-500">
                  色花堂定位为一个集娱乐新闻、影视推荐、社区互动于一体的综合娱乐平台。我们相信，娱乐不只是消遣，更是连接人与人的方式。在这里，你可以浏览最新娱乐资讯、发现优质影视作品，也可以参与社区讨论、分享你的看法。
                </p>
              </div>
              <div className="flex shrink-0 flex-col gap-3 sm:flex-row md:flex-col">
                <Link
                  to="/about"
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  了解更多
                  <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
                </Link>
                <Link
                  to="/community"
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
                >
                  进入社区
                </Link>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}